import { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, SquareArrowOutUpRight, Check } from 'lucide-react';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Select, TextField } from '@components/base/inputs';
import { Alert } from '@components/base/feedback';
import type { EntityFormConfig, FieldConfig } from '@components/integrated/entity-form';
import {
  documentsService,
  type VeosMailAttachment,
  type VeosClassementOption,
  type VeosDocumentRow,
} from '../../api/documentsService';
import {
  base64ByteLength,
  formatFileSize,
  isReceivedEmailContentType,
  openBase64InNewTab,
  resolveContentType,
  stripFileExtension,
} from './documentHelpers';
import {
  ATTACHMENT_GED_CONFIG_ID,
  CLASSEMENT_FIELD,
  buildAttachmentGedConfig,
} from './documentClassement';
import { EmailAttachmentModal } from './EmailAttachmentModal';

interface EmailAttachmentRowProps {
  attachment: VeosMailAttachment;
  classementOptions: VeosClassementOption[];
  /** Already present as a `NOM_ADOC` in the documents list. */
  alreadyInGed: boolean;
  /**
   * The claim's documents list — forwarded to the nested view when this
   * attachment is itself an email (its own rows' "already in GED" state).
   */
  documents: VeosDocumentRow[];
  /** id_sin — the GED entity id for `document-create`. */
  idSin: string;
  /** Resolves the attachment's base64 content (lazy — `data` is null on list). */
  resolveContent: (attachment: VeosMailAttachment) => Promise<string>;
  onGedded: () => void;
  /**
   * Merged (override-applied) config of the "put in GED" controls — the
   * `classement` select, the `libelle` input and the `submit` ("Valider")
   * action — driving their placeholders / accessible names / order /
   * visibility and the button label. `EmailView` always passes the config
   * merged with its `_layoutOverrides`; defaults to the base
   * {@link buildAttachmentGedConfig} for direct renders.
   */
  gedFormConfig?: EntityFormConfig;
}

const OPEN_ERROR = "Erreur lors de l'ouverture de la pièce jointe.";
const GED_ERROR = 'Erreur lors de la mise en GED.';

export function EmailAttachmentRow({
  attachment,
  classementOptions,
  alreadyInGed,
  documents,
  idSin,
  resolveContent,
  onGedded,
  gedFormConfig,
}: Readonly<EmailAttachmentRowProps>) {
  const defaultGedConfig = useMemo(
    () => buildAttachmentGedConfig(ATTACHMENT_GED_CONFIG_ID),
    [],
  );
  const gedConfig = gedFormConfig ?? defaultGedConfig;

  const [cdDs, setCdDs] = useState('');
  // The GED document name (mahhp 22843), preloaded with the file name without
  // its extension. Rows are keyed by attachment id, so a different attachment
  // remounts the row — no reset effect needed.
  const [libelle, setLibelle] = useState(() => stripFileExtension(attachment.name));
  const [isOpening, setIsOpening] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isGedding, setIsGedding] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [byteSize, setByteSize] = useState<number | null>(() => {
    if (attachment.size != null) return attachment.size;
    const fromData = base64ByteLength(attachment.data);
    return fromData > 0 ? fromData : null;
  });

  // Resolve the human-readable size: prefer a known size, else compute it from
  // the (lazily fetched) bytes — the spec requires the size to always show.
  useEffect(() => {
    if (byteSize != null) return;
    let cancelled = false;
    void resolveContent(attachment)
      .then((data) => {
        if (!cancelled) setByteSize(base64ByteLength(data));
      })
      .catch(() => {
        // Size stays hidden if we can't resolve it; open/validate still work.
      });
    return () => {
      cancelled = true;
    };
  }, [attachment, resolveContent, byteSize]);

  const classementSelectOptions = classementOptions.map((o) => ({
    value: o.CD_DS,
    label: o.LIB_DS,
  }));

  const contentType = resolveContentType(attachment.name, attachment.contentType);
  // An attachment that is itself an email (`.msg` / `.eml`) cannot be rendered
  // by the browser — it opens in-app, in a modal, instead of a new tab.
  const isEmail = isReceivedEmailContentType(contentType);

  const handleOpen = useCallback(async () => {
    setIsOpening(true);
    setError(null);
    try {
      const data = await resolveContent(attachment);
      openBase64InNewTab(data, contentType, attachment.name);
    } catch {
      setError(OPEN_ERROR);
    } finally {
      setIsOpening(false);
    }
  }, [attachment, contentType, resolveContent]);

  const handleGed = useCallback(async () => {
    if (cdDs === '') return;
    setIsGedding(true);
    setError(null);
    try {
      const data = await resolveContent(attachment);
      // The GED label is the user-editable libellé (preloaded with the file
      // name without its extension; that default is the fallback should it be
      // blank) — not the selected category's display label (the category is
      // sent as `type`). `name` stays the ORIGINAL file name: it is what
      // `isFileNameInGed` matches against `NOM_ADOC`, so a renamed label must
      // not break the already-in-GED detection after the list refresh.
      const label = libelle.trim() || stripFileExtension(attachment.name);
      await documentsService.putAttachmentInGed({
        contentType,
        datas: data,
        idTab: idSin,
        label,
        name: attachment.name,
        type: cdDs,
      });
      setValidated(true);
      onGedded();
    } catch {
      setError(GED_ERROR);
    } finally {
      setIsGedding(false);
    }
  }, [attachment, cdDs, libelle, contentType, idSin, resolveContent, onGedded]);

  const isInGed = alreadyInGed || validated;
  const sizeLabel = formatFileSize(byteSize);
  const hasCategory = cdDs !== '';
  const canSubmit = hasCategory && libelle.trim() !== '' && !isGedding;

  // "Valider" — label / visibility from the merged config (`setActionLabel` /
  // `hideAction` on the `submit` action key).
  const submitAction = gedConfig.actions?.submit;
  const isSubmitVisible = submitAction?.visible !== false;

  // The two GED inputs, rendered from the merged config by stable key. The
  // config labels are the inputs' accessible names, not visible labels (the
  // mockup shows none); placeholders flow from config, never inline literals.
  const renderGedInput = (name: string, field: FieldConfig) => {
    if (name === CLASSEMENT_FIELD.classement) {
      return (
        <div
          key={name}
          className={`w-44 shrink-0 ${
            hasCategory ? '' : 'rounded-lg ring-1 ring-warning-400'
          }`}
        >
          {/* `Select` has no aria-label pass-through (it only exposes `id` /
              `name` / a visible `label`), so the stable key is passed as its
              `name`. */}
          <Select
            name={name}
            options={classementSelectOptions}
            value={cdDs}
            onChange={(val) => {
              setCdDs(Array.isArray(val) ? (val[0] ?? '') : val);
            }}
            placeholder={field.placeholder}
            size="sm"
          />
        </div>
      );
    }
    if (name === CLASSEMENT_FIELD.libelle) {
      return (
        <div key={name} className="flex-1 min-w-0">
          <TextField
            aria-label={field.label}
            value={libelle}
            onChange={(e) => {
              setLibelle(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isSubmitVisible && canSubmit) {
                e.preventDefault();
                void handleGed();
              }
            }}
            placeholder={field.placeholder}
            size="sm"
          />
        </div>
      );
    }
    return null;
  };

  // Merged-`order` sort, gated on merged `visible` — so `reorderFields` /
  // `hideField` overrides reorder / drop an input (same shape as
  // `DocumentClassementInputs`).
  const orderedGedInputs = (
    [
      [CLASSEMENT_FIELD.classement, gedConfig.fields[CLASSEMENT_FIELD.classement]],
      [CLASSEMENT_FIELD.libelle, gedConfig.fields[CLASSEMENT_FIELD.libelle]],
    ] as Array<[string, FieldConfig | undefined]>
  )
    .filter(
      (entry): entry is [string, FieldConfig] =>
        entry[1] !== undefined && entry[1].visible !== false,
    )
    .sort(
      (a, b) =>
        (a[1].order ?? Number.MAX_SAFE_INTEGER) - (b[1].order ?? Number.MAX_SAFE_INTEGER),
    );

  return (
    <div className="border border-secondary-200 rounded-md px-3 py-2">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-secondary-400 shrink-0" />
        <Text as="span" size="sm" className="truncate min-w-0 flex-1">
          {attachment.name}
        </Text>

        {sizeLabel && (
          <Text as="span" size="xs" color="muted" className="shrink-0 tabular-nums">
            {sizeLabel}
          </Text>
        )}

        {/* Open — end of the first line. An email opens in the modal below;
            any other file in a new tab (or downloads, when the browser cannot
            display it). */}
        <button
          type="button"
          aria-label={
            isEmail
              ? `Ouvrir ${attachment.name}`
              : `Ouvrir ${attachment.name} dans un nouvel onglet`
          }
          onClick={() => {
            if (isEmail) {
              setIsEmailOpen(true);
            } else {
              void handleOpen();
            }
          }}
          disabled={isOpening}
          className="shrink-0 p-1.5 rounded-md text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SquareArrowOutUpRight className="w-4 h-4" />
        </button>
      </div>

      {/* Second line: the GED controls (mahhp 22843), in both states so every
          row keeps the same height. */}
      <div className="mt-2 flex items-center gap-2">
        {isInGed ? (
          // Validated / already-in-GED → read-only: disabled select + ✓ box.
          <>
            <div className="w-44 shrink-0">
              <Select
                options={classementSelectOptions}
                value={validated ? cdDs : ''}
                onChange={() => undefined}
                placeholder={alreadyInGed && !validated ? 'Déjà en GED' : 'Mettre en GED'}
                size="sm"
                disabled
              />
            </div>
            <span
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border border-success-200 bg-success-50 text-success-600"
              aria-label="Pièce jointe mise en GED"
              role="img"
            >
              <Check className="w-4 h-4" />
            </span>
          </>
        ) : (
          <>
            {orderedGedInputs.map(([name, field]) => renderGedInput(name, field))}
            {isSubmitVisible && (
              <Button
                variant={hasCategory ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => {
                  void handleGed();
                }}
                loading={isGedding}
                disabled={!canSubmit}
                className="shrink-0 ml-auto"
              >
                {submitAction?.label ?? 'Valider'}
              </Button>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="mt-2">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {/* Each row owns its modal, so an email attached to the email shown in
          this modal opens one level deeper. */}
      {isEmail && (
        <EmailAttachmentModal
          open={isEmailOpen}
          onClose={() => {
            setIsEmailOpen(false);
          }}
          attachment={attachment}
          classementOptions={classementOptions}
          documents={documents}
          idSin={idSin}
          resolveContent={resolveContent}
          onGedded={onGedded}
        />
      )}
    </div>
  );
}
